import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Lock } from 'phosphor-react';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import useOptionsAlertsData from '../../hooks/useOptionsAlertsData';
import { OptionsAlertRuleCard } from '../../components/options/OptionsAlertRuleCard';
import { OptionsAlertRuleDialog } from '../../components/options/OptionsAlertRuleDialog';
import { isOptionsAlertLimitReached } from '../../components/options/optionsFreemiumUtils';

const SUPPORTED_ALERT_TYPES = [
  'dte_roll_window',
  'leg_delta',
  'position_exposure',
  'profit_target',
  'max_loss',
  'strike_proximity',
  'margin_risk',
];

export default function OptionsAlerts() {
  const { t } = useOptionsLocale();
  const { data, loading, error, busyRuleId, createRule, updateRule, toggleRule, deleteRule, updatePreferences, markRead } = useOptionsAlertsData();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const { isPro, openUpgradeModal } = useOptionsEntitlements();

  const monitor = data?.monitor || null;
  const supportedTypes = useMemo(() => new Set(SUPPORTED_ALERT_TYPES), []);
  const visibleActiveRules = useMemo(() => (data?.active_rules || []).filter((item) => supportedTypes.has(item.alert_type)), [data?.active_rules, supportedTypes]);
  const visiblePausedRules = useMemo(() => (data?.paused_rules || []).filter((item) => supportedTypes.has(item.alert_type)), [data?.paused_rules, supportedTypes]);
  const visibleHistory = useMemo(() => (data?.history || []).filter((item) => supportedTypes.has(item.alert_type)), [data?.history, supportedTypes]);
  const hasAnyRules = visibleActiveRules.length > 0 || visiblePausedRules.length > 0;
  const freeLimitReached = isOptionsAlertLimitReached({ isPro, activeCount: visibleActiveRules.length });
  const emailEnabled = !!data?.preferences?.email_enabled;
  const monitorState = getMonitorPresentation(monitor, t);

  const handleSave = async (payload) => {
    const isEnablingPausedRule = editingRule && !editingRule.enabled && payload.enabled;
    const isCreatingActiveRule = !editingRule && payload.enabled;
    
    if ((isEnablingPausedRule || isCreatingActiveRule) && !isPro && visibleActiveRules.length >= 1) {
      openUpgradeModal("ALERTS");
      return;
    }

    const success = editingRule
      ? await updateRule(editingRule.id, payload)
      : await createRule(payload);
    if (success) {
      toast.success(t('optionsAlerts.savedToast'));
      setDialogOpen(false);
      setEditingRule(null);
    }
  };

  const handleToggle = async (rule) => {
    if (!rule.enabled && !isPro && visibleActiveRules.length >= 1) {
      openUpgradeModal("ALERTS");
      return;
    }
    const success = await toggleRule(rule.id, !rule.enabled);
    if (success) toast.success(rule.enabled ? t('optionsAlerts.pausedToast') : t('optionsAlerts.activatedToast'));
  };

  const handleDelete = async (rule) => {
    const success = await deleteRule(rule.id);
    if (success) toast.success(t('optionsAlerts.deletedToast'));
  };

  const handleMarkRead = async (eventId) => {
    const success = await markRead(eventId);
    if (success) toast.success(t('optionsAlerts.markedReadToast'));
  };

  const handlePreferencesToggle = async (checked) => {
    const success = await updatePreferences({
      email_enabled: checked,
      locale: data?.preferences?.locale || 'ptBR',
      timezone: data?.preferences?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    });
    if (success) toast.success(t('optionsAlerts.preferencesSavedToast'));
  };

  const handleCreateClick = () => {
    if (!isPro && visibleActiveRules.length >= 1) {
      openUpgradeModal("ALERTS");
      return;
    }
    setEditingRule(null);
    setDialogOpen(true);
  };

  return (
    <OptionsPageShell title={t('optionsAlerts.pageTitle')} description={t('optionsAlerts.pageDescription')} hideHeader data-testid="options-alerts-page">
      {loading ? (
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-white/60" data-testid="options-alerts-loading">
          {t('optionsAlerts.loading')}
        </div>
      ) : (
        <div className="space-y-5" data-testid="options-alerts-content">
          <section className="max-w-3xl space-y-2" data-testid="options-alerts-compact-header">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-white/42" data-typography="label" data-testid="options-alerts-header-eyebrow">{t('optionsAlerts.pageTitle')}</p>
            <h1 className="text-3xl font-black tracking-tight text-white sm:text-4xl" data-typography="heading" data-testid="options-alerts-header-title">{t('optionsAlerts.pageTitle')}</h1>
            <p className="text-sm text-white/60 sm:text-base" data-testid="options-alerts-header-description">{t('optionsAlerts.pageDescription')}</p>
          </section>

          <section className="space-y-3" data-testid="options-alerts-overview-section">
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="options-alerts-summary-grid">
              <SummaryCard label={t('optionsAlerts.active')} value={String(visibleActiveRules.length)} testId="options-alerts-summary-active" />
              <SummaryCard label={t('optionsAlerts.paused')} value={String(visiblePausedRules.length)} testId="options-alerts-summary-paused" />
              <SummaryCard label={t('optionsAlerts.recentlyTriggered')} value={String(visibleHistory.length)} testId="options-alerts-summary-history" />
              <EmailSummaryCard
                email={data?.preferences?.email || t('optionsAlerts.emailNotConfigured')}
                emailEnabled={emailEnabled}
                onToggle={handlePreferencesToggle}
                t={t}
              />
            </div>

            <div className={`flex flex-wrap items-start gap-3 rounded-2xl border px-4 py-3 ${monitorState.wrapperClassName}`} data-testid="options-alerts-monitor-status">
              <span className={`mt-1 inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${monitorState.dotClassName}`} data-testid="options-alerts-monitor-status-dot" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white" data-testid="options-alerts-monitor-status-label">{monitorState.label}</p>
                {monitorState.note ? <p className="mt-1 text-xs text-white/62" data-testid="options-alerts-monitor-status-note">{monitorState.note}</p> : null}
              </div>
            </div>
          </section>

          {error ? <p className="text-sm text-red-300" data-testid="options-alerts-error">{error}</p> : null}

          <div className="flex flex-wrap items-center justify-between gap-3" data-testid="options-alerts-rules-header">
            <div>
              <h2 className="text-xl font-black text-white" data-typography="heading" data-testid="options-alerts-active-title">{t('optionsAlerts.manageTitle')}</h2>
              <p className="text-sm text-white/58" data-testid="options-alerts-active-description">{t('optionsAlerts.manageDescription')}</p>
              {!isPro ? (
                <p className="mt-1 text-xs text-[#F0B90B]/80" data-testid="options-alerts-free-limit-message">
                  FREE: 1 alerta ativo. O PRO permite mais alertas simultâneos.
                </p>
              ) : null}
            </div>
            <Button type="button" className="rounded-full bg-[#F0B90B] px-5 text-black hover:bg-[#ffd26a] flex items-center gap-1.5" onClick={handleCreateClick} data-testid="options-alerts-create-button">
              {freeLimitReached && <Lock size={12} weight="fill" />}
              <Plus size={16} />
              {t('optionsAlerts.createAlert')}
              {freeLimitReached && <span className="text-[10px] bg-black/20 text-black px-1.5 py-0.5 rounded-full font-bold" data-testid="options-alerts-create-pro-badge">PRO</span>}
            </Button>
          </div>

          <Tabs defaultValue="active" className="space-y-4" data-testid="options-alerts-tabs">
            <TabsList className="rounded-full border border-white/10 bg-white/[0.04] p-1">
              <TabsTrigger value="active" className="rounded-full px-4 data-[state=active]:bg-[#F0B90B] data-[state=active]:text-black" data-testid="options-alerts-active-tab-trigger">{t('optionsAlerts.activeTab')}</TabsTrigger>
              <TabsTrigger value="history" className="rounded-full px-4 data-[state=active]:bg-[#F0B90B] data-[state=active]:text-black" data-testid="options-alerts-history-tab-trigger">{t('optionsAlerts.historyTab')}</TabsTrigger>
            </TabsList>

            <TabsContent value="active" className="space-y-6" data-testid="options-alerts-active-tab">
              <section className="space-y-3" data-testid="options-alerts-active-rules-section">
                {visibleActiveRules.length > 0 ? (
                  <div className="grid gap-4">
                    {visibleActiveRules.map((rule) => (
                      <OptionsAlertRuleCard key={rule.id} rule={rule} busy={busyRuleId === rule.id} onEdit={(item) => { setEditingRule(item); setDialogOpen(true); }} onToggle={handleToggle} onDelete={handleDelete} />
                    ))}
                  </div>
                ) : !hasAnyRules ? (
                  <EmptyState
                    title={t('optionsAlerts.emptyConfiguredTitle')}
                    description={t('optionsAlerts.emptyConfiguredDescription')}
                    ctaLabel={t('optionsAlerts.createAlert')}
                    onCtaClick={handleCreateClick}
                    testId="options-alerts-empty-active"
                  />
                ) : null}
              </section>

              {visiblePausedRules.length ? (
                <section className="space-y-3" data-testid="options-alerts-paused-section">
                  <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-white/45" data-typography="label">{t('optionsAlerts.paused')}</h3>
                  <div className="grid gap-4">
                    {visiblePausedRules.map((rule) => (
                      <OptionsAlertRuleCard key={rule.id} rule={rule} busy={busyRuleId === rule.id} onEdit={(item) => { setEditingRule(item); setDialogOpen(true); }} onToggle={handleToggle} onDelete={handleDelete} />
                    ))}
                  </div>
                </section>
              ) : null}
            </TabsContent>

            <TabsContent value="history" className="space-y-3" data-testid="options-alerts-history-tab">
              {visibleHistory.length ? (
                <div className="grid gap-3">
                  {visibleHistory.map((event) => (
                    <Card key={event.id} className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid={`options-alert-history-event-${event.id}`}>
                      <CardContent className="flex flex-wrap items-start justify-between gap-4 p-4">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-white">{event.title}</p>
                            {!event.read_at ? <Badge className="bg-[#F0B90B]/15 text-[#F0B90B]">{t('optionsAlerts.unread')}</Badge> : null}
                          </div>
                          <p className="text-sm text-white/60">{event.message}</p>
                          <p className="text-xs uppercase tracking-[0.18em] text-white/40">{event.observed_value_label} · {event.threshold_label}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!event.read_at ? (
                            <Button type="button" variant="ghost" className="text-white/70 hover:bg-white/10 hover:text-white" onClick={() => handleMarkRead(event.id)} data-testid={`options-alert-history-mark-read-${event.id}`}>
                              {t('optionsAlerts.markRead')}
                            </Button>
                          ) : null}
                          <Button asChild variant="ghost" className="text-white/70 hover:bg-white/10 hover:text-white" data-testid={`options-alert-history-open-${event.id}`}>
                            <a href={event.cta_path || '/options/alerts'}>{t('optionsAlerts.open')}</a>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <EmptyState title={t('optionsAlerts.emptyHistoryTitle')} description={t('optionsAlerts.emptyHistoryDescription')} testId="options-alerts-empty-history" />
              )}
            </TabsContent>
          </Tabs>

          <OptionsAlertRuleDialog open={dialogOpen} onOpenChange={setDialogOpen} onSave={handleSave} initialRule={editingRule} targets={data?.targets} preferences={data?.preferences} />
        </div>
      )}
    </OptionsPageShell>
  );
}

const SummaryCard = ({ label, value, testId }) => (
  <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid={testId}>
    <CardContent className="space-y-1 p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-white/42" data-typography="label" data-testid={`${testId}-label`}>{label}</p>
      <p className="text-2xl font-black tracking-tight text-white" data-typography="metric" data-testid={`${testId}-value`}>{value}</p>
    </CardContent>
  </Card>
);

const EmailSummaryCard = ({ email, emailEnabled, onToggle, t }) => (
  <Card className="border-white/10 bg-white/[0.03] text-white shadow-none" data-testid="options-alerts-email-card">
    <CardContent className="space-y-3 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-white/42" data-typography="label" data-testid="options-alerts-email-card-label">{t('optionsAlerts.emailAlerts')}</p>
          <p className="mt-1 text-2xl font-black tracking-tight text-white" data-typography="metric" data-testid="options-alerts-email-card-value">{emailEnabled ? t('optionsAlerts.emailOn') : t('optionsAlerts.emailOff')}</p>
        </div>
        <Switch checked={emailEnabled} onCheckedChange={onToggle} className="data-[state=checked]:bg-[#22C55E] data-[state=unchecked]:bg-white/20" data-testid="options-alerts-email-enabled" />
      </div>
      <div className="min-w-0">
        <p className="truncate text-xs text-white/55" data-testid="options-alerts-email-card-address">{email}</p>
        <p className="mt-1 text-xs text-white/62" data-testid="options-alerts-email-card-status">{emailEnabled ? t('optionsAlerts.emailEnabledState') : t('optionsAlerts.emailDisabledState')}</p>
      </div>
    </CardContent>
  </Card>
);

const EmptyState = ({ title, description, ctaLabel, onCtaClick, testId }) => (
  <Card className="border-white/10 bg-black/20 text-white shadow-none" data-testid={testId}>
    <CardContent className="space-y-3 px-6 py-10 text-center">
      <p className="text-lg font-semibold text-white" data-testid={`${testId}-title`}>{title}</p>
      <p className="text-sm text-white/58" data-testid={`${testId}-description`}>{description}</p>
      {ctaLabel && onCtaClick ? (
        <div className="flex justify-center">
          <Button type="button" className="rounded-full bg-[#F0B90B] px-5 text-black hover:bg-[#ffd26a]" onClick={onCtaClick} data-testid={`${testId}-cta`}>
            <Plus size={16} className="mr-2" />
            {ctaLabel}
          </Button>
        </div>
      ) : null}
    </CardContent>
  </Card>
);

const getMonitorPresentation = (monitor, t) => {
  const status = monitor?.last_check_status;
  if (status === 'connection_required') {
    return {
      label: t('optionsAlerts.monitoringConnectionRequired'),
      note: t('optionsAlerts.monitoringErrorConnection'),
      wrapperClassName: 'border-amber-500/20 bg-amber-500/8',
      dotClassName: 'bg-amber-400',
    };
  }
  if (status === 'refresh_failed' || status === 'delayed') {
    return {
      label: t('optionsAlerts.monitoringDelayed'),
      note: monitor?.last_error || t('optionsAlerts.monitoringErrorRefresh'),
      wrapperClassName: 'border-amber-500/20 bg-amber-500/8',
      dotClassName: 'bg-amber-400',
    };
  }
  return {
    label: t('optionsAlerts.monitoringActive'),
    note: null,
    wrapperClassName: 'border-emerald-500/20 bg-emerald-500/8',
    dotClassName: 'bg-emerald-400',
  };
};